import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Store, TrendingUp, Crown, Package, BarChart3, Mail, MessageCircle, Headphones, Star, Loader2, Eye, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VENDOR_PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    subtitle: 'Inizia a vendere',
    price: 30,
    icon: Store,
    color: 'from-emerald-500 to-emerald-600',
    features: [
      { text: 'Fino a 50 prodotti', included: true },
      { text: 'Badge Venditore Verificato', included: true, highlight: true },
      { text: '25% commissione sulle vendite', included: true },
      { text: '3 prodotti in evidenza/mese', included: true },
      { text: 'Dashboard vendite', included: true },
      { text: 'Supporto via email', included: true },
      { text: 'Pagamenti sicuri garantiti', included: true },
      { text: 'Cancella quando vuoi', included: true },
    ],
    commission: 25,
    maxProducts: 50,
    featuredProducts: 3,
  },
  {
    id: 'business',
    name: 'Business',
    subtitle: 'Per chi vende regolarmente',
    price: 149,
    icon: TrendingUp,
    color: 'from-blue-500 to-blue-600',
    popular: true,
    features: [
      { text: 'Fino a 300 prodotti', included: true },
      { text: 'Badge Venditore Verificato', included: true, highlight: true },
      { text: 'Solo 12% commissione', included: true, highlight: true },
      { text: '15 prodotti in evidenza/mese', included: true },
      { text: 'Dashboard vendite avanzata', included: true },
      { text: 'Supporto prioritario Email + Chat', included: true },
      { text: 'Statistiche e analytics', included: true },
      { text: 'Promozione sui nostri canali', included: true },
    ],
    commission: 12,
    maxProducts: 300,
    featuredProducts: 15,
  },
  {
    id: 'premium',
    name: 'Premium',
    subtitle: 'Per venditori professionisti',
    price: 299,
    icon: Crown,
    color: 'from-amber-500 to-amber-600',
    features: [
      { text: 'Prodotti ILLIMITATI', included: true, highlight: true },
      { text: 'Badge Venditore Verificato', included: true },
      { text: '0% COMMISSIONI - Tieni tutto!', included: true, highlight: true },
      { text: '50 prodotti in evidenza/mese', included: true },
      { text: 'Dashboard vendite completa', included: true },
      { text: 'Account manager dedicato', included: true, highlight: true },
      { text: 'Supporto telefonico prioritario', included: true },
      { text: 'Promozione premium sui social', included: true },
    ],
    commission: 0,
    maxProducts: -1, // unlimited
    featuredProducts: 50,
  },
];

const SellPage = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    password: '',
    phone: '',
    description: '',
  });

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setShowRegisterModal(true);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!formData.business_name || !formData.email || !formData.password) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setLoading(true);
    try {
      // Register vendor
      const registerRes = await axios.post(`${API}/vendor/register`, {
        ...formData,
        plan: selectedPlan.id,
      });

      // Create PayPal payment for subscription
      const paymentRes = await axios.post(`${API}/paypal/create-vendor-subscription`, {
        vendor_id: registerRes.data.vendor_id,
        plan_id: selectedPlan.id,
        plan_name: selectedPlan.name,
        amount: selectedPlan.price,
      });

      if (paymentRes.data.approval_url) {
        toast.success('Registrazione completata! Reindirizzamento a PayPal...');
        window.location.href = paymentRes.data.approval_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#064E3B] via-[#065F46] to-[#047857] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-300 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block bg-white/20 backdrop-blur-sm text-sm font-medium px-4 py-2 rounded-full mb-6">
              Programma Venditori QANTRI
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Vendi su QANTRI
            </h1>
            <p className="text-xl text-emerald-100 mb-8">
              Esponi i tuoi prodotti in un marketplace di lusso. Raggiungi clienti che cercano qualità e stile.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-emerald-300" />
                <span>Badge Venditore Verificato</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-emerald-300" />
                <span>Pagamenti sicuri garantiti</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-emerald-300" />
                <span>Supporto dedicato</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ fontFamily: 'Playfair Display, serif' }}>
            Come Funziona
          </h2>
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { step: '1', title: 'Registrati', desc: 'Scegli il piano adatto a te e completa la registrazione' },
              { step: '2', title: 'Carica Prodotti', desc: 'Aggiungi i tuoi prodotti con foto e descrizioni' },
              { step: '3', title: 'Vendi', desc: 'I clienti acquistano, tu ricevi l\'ordine' },
              { step: '4', title: 'Guadagna', desc: 'Evadi l\'ordine e incassa i tuoi guadagni' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-[#064E3B] text-white text-2xl font-bold rounded-full flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-neutral-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Sell on QANTRI */}
      <section className="py-16 bg-neutral-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Perché Vendere su QANTRI
            </h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Un marketplace pensato per valorizzare i tuoi prodotti
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="bg-white p-6 rounded-xl shadow-sm text-center"
            >
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Eye className="h-7 w-7 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Visibilità Premium</h3>
              <p className="text-neutral-600 text-sm">
                I tuoi prodotti esposti in un ambiente curato e professionale, insieme a brand di qualità.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white p-6 rounded-xl shadow-sm text-center"
            >
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Star className="h-7 w-7 text-blue-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Clienti Selezionati</h3>
              <p className="text-neutral-600 text-sm">
                Raggiungi clienti che cercano qualità e sono disposti a pagare per prodotti di valore.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white p-6 rounded-xl shadow-sm text-center"
            >
              <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="h-7 w-7 text-amber-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Gestione Semplice</h3>
              <p className="text-neutral-600 text-sm">
                Dashboard intuitiva per gestire prodotti, ordini e guadagni. Tu pensi solo a vendere.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Scegli il Tuo Piano
            </h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Abbonamento mensile semplice. Scegli il piano più adatto alle tue esigenze.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {VENDOR_PLANS.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-white rounded-2xl shadow-lg overflow-hidden ${plan.popular ? 'ring-2 ring-[#064E3B] scale-105' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-[#064E3B] text-white text-center py-2 text-sm font-medium">
                    Più Popolare
                  </div>
                )}
                
                <div className={`p-6 ${plan.popular ? 'pt-12' : ''}`}>
                  {/* Header */}
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4`}>
                    <plan.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-1">{plan.name}</h3>
                  <p className="text-neutral-500 text-sm mb-4">{plan.subtitle}</p>
                  
                  {/* Price */}
                  <div className="mb-6">
                    <span className="text-4xl font-bold">€{plan.price}</span>
                    <span className="text-neutral-500">/mese</span>
                    {plan.commission > 0 ? (
                      <p className="text-neutral-600 text-sm font-medium mt-1">
                        + {plan.commission}% commissione sulle vendite
                      </p>
                    ) : (
                      <p className="text-emerald-600 text-sm font-semibold mt-1">
                        0% commissioni - Tieni tutto tu!
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        {feature.included ? (
                          <Check className={`h-5 w-5 flex-shrink-0 ${feature.highlight ? 'text-emerald-500' : 'text-neutral-400'}`} />
                        ) : (
                          <X className="h-5 w-5 flex-shrink-0 text-neutral-300" />
                        )}
                        <span className={`text-sm ${feature.included ? (feature.highlight ? 'text-emerald-700 font-medium' : 'text-neutral-700') : 'text-neutral-400'}`}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Button
                    onClick={() => handleSelectPlan(plan)}
                    className={`w-full ${plan.popular ? 'bg-[#064E3B] hover:bg-[#053d2f]' : 'bg-neutral-800 hover:bg-neutral-900'}`}
                    data-testid={`select-plan-${plan.id}`}
                  >
                    Inizia con {plan.name}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Garanzie */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="mt-12 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-2xl p-8 max-w-4xl mx-auto"
          >
            <h3 className="text-xl font-bold text-center mb-6">Le Nostre Garanzie</h3>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl mb-2">🔒</div>
                <h4 className="font-semibold mb-1">Pagamenti Sicuri</h4>
                <p className="text-sm text-neutral-600">Transazioni protette al 100%. I tuoi guadagni sono al sicuro.</p>
              </div>
              <div>
                <div className="text-3xl mb-2">🚫</div>
                <h4 className="font-semibold mb-1">Nessun Vincolo</h4>
                <p className="text-sm text-neutral-600">Cancella il tuo abbonamento in qualsiasi momento, senza penali.</p>
              </div>
              <div>
                <div className="text-3xl mb-2">💬</div>
                <h4 className="font-semibold mb-1">Supporto Sempre</h4>
                <p className="text-sm text-neutral-600">Il nostro team ti aiuta a vendere di più e meglio.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quanto puoi guadagnare */}
      <section className="py-16 bg-neutral-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Quanto Puoi Guadagnare?
            </h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Ecco alcuni esempi concreti in base al tuo volume di vendite mensile
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-6 shadow-sm border-2 border-emerald-100"
            >
              <div className="text-center">
                <p className="text-sm text-neutral-500 mb-1">Se vendi</p>
                <p className="text-3xl font-bold text-emerald-600 mb-4">€1.000/mese</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-2 border-b">
                    <span>Con Starter (€30)</span>
                    <span className="font-semibold">Tieni €720</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span>Con Business (€149)</span>
                    <span className="font-semibold">Tieni €731</span>
                  </div>
                  <div className="flex justify-between py-2 bg-amber-50 -mx-2 px-2 rounded">
                    <span>Con Premium (€299)</span>
                    <span className="font-semibold text-amber-600">Tieni €701</span>
                  </div>
                </div>
                <p className="text-xs text-neutral-500 mt-4">💡 Starter è il migliore per te</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-6 shadow-sm border-2 border-blue-200 scale-105"
            >
              <div className="text-center">
                <p className="text-sm text-neutral-500 mb-1">Se vendi</p>
                <p className="text-3xl font-bold text-blue-600 mb-4">€3.000/mese</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-2 border-b">
                    <span>Con Starter (€30)</span>
                    <span className="font-semibold">Tieni €2.220</span>
                  </div>
                  <div className="flex justify-between py-2 border-b bg-blue-50 -mx-2 px-2 rounded">
                    <span>Con Business (€149)</span>
                    <span className="font-semibold text-blue-600">Tieni €2.491</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span>Con Premium (€299)</span>
                    <span className="font-semibold">Tieni €2.701</span>
                  </div>
                </div>
                <p className="text-xs text-neutral-500 mt-4">💡 Business o Premium per te</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-6 shadow-sm border-2 border-amber-200"
            >
              <div className="text-center">
                <p className="text-sm text-neutral-500 mb-1">Se vendi</p>
                <p className="text-3xl font-bold text-amber-600 mb-4">€5.000/mese</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-2 border-b">
                    <span>Con Starter (€30)</span>
                    <span className="font-semibold">Tieni €3.720</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span>Con Business (€149)</span>
                    <span className="font-semibold">Tieni €4.251</span>
                  </div>
                  <div className="flex justify-between py-2 bg-amber-50 -mx-2 px-2 rounded">
                    <span>Con Premium (€299)</span>
                    <span className="font-semibold text-amber-600">Tieni €4.701</span>
                  </div>
                </div>
                <p className="text-xs text-neutral-500 mt-4">💡 Premium è il migliore per te</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ fontFamily: 'Playfair Display, serif' }}>
            Domande Frequenti
          </h2>
          <div className="space-y-6">
            {[
              {
                q: 'Cosa posso vendere su QANTRI?',
                a: 'Puoi vendere prodotti di moda, accessori, arredamento, elettronica e lifestyle. Accettiamo sia prodotti nuovi che vintage di qualità. Non sono ammessi prodotti contraffatti.'
              },
              {
                q: 'Come funziona la spedizione?',
                a: 'Tu gestisci la spedizione. Quando un cliente acquista, ricevi l\'ordine con l\'indirizzo. Spedisci il prodotto e carichi il tracking. Puoi usare qualsiasi corriere.'
              },
              {
                q: 'Quando ricevo i pagamenti?',
                a: 'I pagamenti vengono elaborati ogni settimana via bonifico o PayPal. Ricevi il totale delle tue vendite meno la commissione QANTRI (se prevista dal tuo piano).'
              },
              {
                q: 'Posso cambiare piano?',
                a: 'Sì, puoi fare upgrade o downgrade in qualsiasi momento dalla tua dashboard. Nessuna penale.'
              },
              {
                q: 'Posso cancellare l\'abbonamento?',
                a: 'Sì, puoi cancellare quando vuoi. Non ci sono vincoli o penali. I tuoi prodotti verranno rimossi alla scadenza del mese pagato.'
              },
              {
                q: 'Come funziona il Badge Venditore Verificato?',
                a: 'Il badge appare accanto ai tuoi prodotti e aumenta la fiducia dei clienti. Tutti i piani lo includono.'
              },
            ].map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                className="border-b border-neutral-200 pb-6"
              >
                <h3 className="font-semibold text-lg mb-2">{faq.q}</h3>
                <p className="text-neutral-600">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      {showRegisterModal && selectedPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-bold">Registrati come Venditore</h3>
                  <p className="text-sm text-neutral-500">Piano {selectedPlan.name} - €{selectedPlan.price}</p>
                </div>
                <button 
                  onClick={() => setShowRegisterModal(false)}
                  className="p-2 hover:bg-neutral-100 rounded-full"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nome Attività *</label>
                  <Input
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleInputChange}
                    placeholder="Es. Mario's Fashion"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Nome e Cognome *</label>
                  <Input
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleInputChange}
                    placeholder="Mario Rossi"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="mario@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Password *</label>
                  <Input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Minimo 8 caratteri"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Telefono</label>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+39 333 1234567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Descrizione Attività</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Descrivi brevemente cosa vendi..."
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#064E3B]"
                    rows={3}
                  />
                </div>

                <div className="bg-emerald-50 p-4 rounded-lg">
                  <p className="text-sm text-emerald-800">
                    <strong>Riepilogo:</strong><br />
                    • Quota iscrizione: €{selectedPlan.price}<br />
                    • Primo mese: GRATIS (0% commissione)<br />
                    • Dal 2° mese: {selectedPlan.commission}% sulle vendite
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#064E3B] hover:bg-[#053d2f]"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Elaborazione...
                    </>
                  ) : (
                    <>Procedi al Pagamento - €{selectedPlan.price}</>
                  )}
                </Button>

                <p className="text-xs text-center text-neutral-500">
                  Cliccando "Procedi al Pagamento" accetti i Termini di Servizio per Venditori
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SellPage;
